import type { Role } from '@repo/shared-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { computeQuoteOutput } from '../lib/calculator-math';
import {
  CALCULATOR_STORAGE_KEY,
  readPersistedInput,
  writePersistedInput,
} from '../lib/calculator-persistence';
import {
  type CalculatorProduct,
  EMPTY_QUOTE_INPUT,
  type QuoteInput,
  type QuoteOutput,
} from '../types/calculator.types';

/**
 * useCalculatorState — owns the calculator's input state, derives the
 * output via `useMemo` (so it recomputes only when inputs/product/role
 * change), and persists the input to localStorage with a 500ms debounce.
 *
 * Slice 8 PR#1 (base) + polish PR installment (financing term +
 * installment output). localStorage key `globaleserp-calculator-v1`.
 * The persisted envelope is tenant-scoped: on a tenant switch the
 * stored state is discarded and the form resets (reset-on-tenant-change),
 * mirroring the Slice 0 Zustand-persist + debounce pattern.
 *
 * The output math lives in `../lib/calculator-math.ts` (SSOT) and the
 * persistence in `../lib/calculator-persistence.ts` (both unit-tested).
 *
 * Polish PR installment notes:
 *   - The installment figure is derived from the closed `FINANCING_TERMS`
 *     table by the math engine; the hook only forwards `termMonths`.
 *   - Commission was removed from the calculator entirely; this hook
 *     no longer takes a `commissionPercent` argument.
 */
export { CALCULATOR_STORAGE_KEY };
const PERSIST_DEBOUNCE_MS = 500;

export interface UseCalculatorStateParams {
  role: Role;
  tenantId: string;
  /** Resolved product for the selected `productId`, or null. */
  product: CalculatorProduct | null;
}

export interface UseCalculatorStateResult {
  input: QuoteInput;
  output: QuoteOutput;
  lastUpdated: number;
  setInput: (patch: Partial<QuoteInput>) => void;
  reset: () => void;
}

export function useCalculatorState(params: UseCalculatorStateParams): UseCalculatorStateResult {
  const { role, tenantId, product } = params;

  const [input, setInputState] = useState<QuoteInput>(
    () => readPersistedInput(tenantId) ?? EMPTY_QUOTE_INPUT,
  );
  const [lastUpdated, setLastUpdated] = useState<number>(() => Date.now());
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset-on-tenant-change: discard prior state and re-hydrate for the
  // new tenant (which resolves to EMPTY when nothing is stored).
  const prevTenant = useRef(tenantId);
  useEffect(() => {
    if (prevTenant.current === tenantId) return;
    prevTenant.current = tenantId;
    setInputState(readPersistedInput(tenantId) ?? EMPTY_QUOTE_INPUT);
    setLastUpdated(Date.now());
  }, [tenantId]);

  // Debounced persist on input change.
  useEffect(() => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      writePersistedInput(tenantId, input);
    }, PERSIST_DEBOUNCE_MS);
    return () => {
      if (persistTimer.current) clearTimeout(persistTimer.current);
    };
  }, [input, tenantId]);

  const setInput = useCallback((patch: Partial<QuoteInput>) => {
    setInputState((prev) => ({ ...prev, ...patch }));
    setLastUpdated(Date.now());
  }, []);

  const reset = useCallback(() => {
    setInputState(EMPTY_QUOTE_INPUT);
    setLastUpdated(Date.now());
    writePersistedInput(tenantId, EMPTY_QUOTE_INPUT);
  }, [tenantId]);

  const output = useMemo<QuoteOutput>(
    () =>
      computeQuoteOutput({
        unitPrice: product?.defaultSalePrice ?? 0,
        unitCost: product?.defaultCost ?? null,
        quantity: input.quantity,
        discountPercent: input.discountPercent,
        exchangeRate: input.exchangeRate,
        includeTax: input.includeTax,
        termMonths: input.termMonths,
        role,
      }),
    [input, product, role],
  );

  return { input, output, lastUpdated, setInput, reset };
}
